import moment from "moment-timezone";
import { useCenterConstants } from "./use-center/use-center-constants";

/**
 * Utility function to convert UTC date to timezone
 * Can be used outside of React components
 */
export const convertUtcToTimezone = (utcDateString: string | undefined | null, timezone: string): string => {
  if (!utcDateString) {
    return "";
  }
  const converted = moment.utc(utcDateString).tz(timezone);
  return converted.isValid() ? converted.format() : "";
};

/**
 * Utility function to format UTC date in timezone
 */
export const formatUtcInTimezone = (
  utcDateString: string | undefined | null,
  timezone: string,
  format: string
): string => {
  if (!utcDateString) {
    return "";
  }
  const converted = moment.utc(utcDateString).tz(timezone);
  return converted.isValid() ? converted.format(format) : "";
};

/**
 * Utility function to convert date to Date object in timezone
 */
export const convertUtcToDate = (utcDateString: string | undefined | null, timezone: string): Date | null => {
  if (!utcDateString) {
    return null;
  }
  const converted = moment.utc(utcDateString).tz(timezone);
  return converted.isValid() ? converted.toDate() : null;
};

/**
 * Generic utility to convert date fields in an object
 */
export const convertDateFields = <T extends Record<string, unknown>>(
  obj: T,
  dateFields: (keyof T)[],
  timezone: string,
  format?: string
): T => {
  const result = { ...obj };
  dateFields.forEach(field => {
    const value = obj[field];
    if (value) {
      result[field] = format
        ? (formatUtcInTimezone(value as string, timezone, format) as T[keyof T])
        : (convertUtcToTimezone(value as string, timezone) as T[keyof T]);
    }
  });
  return result;
};

/**
 * Convert array of objects with date fields
 */
export const convertDateFieldsInArray = <T extends Record<string, unknown>>(
  array: T[],
  dateFields: (keyof T)[],
  timezone: string,
  format?: string
): T[] => {
  return array.map(item => convertDateFields(item, dateFields, timezone, format));
};

/**
 * Hook to get timezone and conversion utilities for a specific center
 * @param centerId - The ID of the center to get timezone for
 * @returns Object with timezone and conversion utilities
 */
export const useTimezoneConverter = (centerId?: string) => {
  const { activeConstants } = useCenterConstants(centerId);
  const timezone = activeConstants.timezone || "UTC";

  return {
    timezone,
    convertToTimezone: (utcDateString: string | undefined | null) => convertUtcToTimezone(utcDateString, timezone),
    formatInTimezone: (utcDateString: string | undefined | null, format: string) =>
      formatUtcInTimezone(utcDateString, timezone, format),
    convertToDate: (utcDateString: string | undefined | null) => convertUtcToDate(utcDateString, timezone),
    convertDateFields: <T extends Record<string, unknown>>(obj: T, dateFields: (keyof T)[], format?: string) =>
      convertDateFields(obj, dateFields, timezone, format),
    convertDateFieldsInArray: <T extends Record<string, unknown>>(
      array: T[],
      dateFields: (keyof T)[],
      format?: string
    ) => convertDateFieldsInArray(array, dateFields, timezone, format)
  };
};
